import { useEffect, useRef, useState } from "react";
import { testimonials } from "@/mocks/collections";
import Reveal from "@/components/base/Reveal";

const AUTO_MS = 6500;

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const startRef = useRef<number>(performance.now());
  const accumRef = useRef<number>(0);

  const t = testimonials[active];

  // Auto-advance with smooth progress
  useEffect(() => {
    let raf = 0;
    const tick = (now: number) => {
      if (!paused) {
        const elapsed = now - startRef.current + accumRef.current;
        const p = Math.min(1, elapsed / AUTO_MS);
        setProgress(p);
        if (p >= 1) {
          accumRef.current = 0;
          startRef.current = performance.now();
          setActive((prev) => (prev + 1) % testimonials.length);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const jumpTo = (i: number) => {
    accumRef.current = 0;
    startRef.current = performance.now();
    setProgress(0);
    setActive(i);
  };

  const handleEnter = () => {
    accumRef.current = accumRef.current + (performance.now() - startRef.current);
    setPaused(true);
  };
  const handleLeave = () => {
    startRef.current = performance.now();
    setPaused(false);
  };

  const next = () => jumpTo((active + 1) % testimonials.length);
  const prev = () => jumpTo((active - 1 + testimonials.length) % testimonials.length);

  return (
    <section
      id="voices"
      className="relative w-full bg-[#e6f0ff] border-t border-[var(--lum-gold)]/12 py-24 md:py-32 px-6 md:px-12"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Reveal className="max-w-5xl mx-auto text-center">
        <div className="font-label text-[11px] text-[var(--lum-gold)] mb-8">
          よく聞かれること / Q&amp;A
        </div>

        <i className="ri-question-line w-12 h-12 flex items-center justify-center text-[var(--lum-gold)]/70 text-5xl mx-auto mb-8" />

        {/* Question */}
        <div
          key={`q-${active}`}
          className="lum-quote-in font-jp text-base md:text-lg text-[var(--lum-gold)] font-medium mb-6 tracking-wide"
        >
          {t.name}
        </div>

        {/* Answer */}
        <blockquote
          key={`a-${active}`}
          className="lum-quote-in font-jp text-2xl md:text-4xl lg:text-[44px] leading-[1.7] text-[var(--lum-cream)] max-w-4xl mx-auto mb-12"
        >
          {t.quote}
        </blockquote>

        <div key={`meta-${active}`} className="lum-quote-in flex flex-col items-center gap-1.5 mb-12">
          <div className="font-label text-[10px] text-[var(--lum-cream)]/50">
            {t.role} · {t.city}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={prev}
            aria-label="前の質問"
            className="group w-10 h-10 flex items-center justify-center rounded-full border border-[var(--lum-cream)]/20 text-[var(--lum-cream)]/60 hover:text-[var(--lum-gold)] hover:border-[var(--lum-gold)] transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base transition-transform duration-300 group-hover:-translate-x-0.5" />
          </button>

          <div className="flex items-center gap-3">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`質問 ${i + 1}`}
                onClick={() => jumpTo(i)}
                className="group relative h-px w-14 cursor-pointer"
              >
                <span
                  className={`absolute inset-0 transition-all duration-500 ${
                    active === i
                      ? "bg-[var(--lum-cream)]/20"
                      : "bg-[var(--lum-cream)]/20 group-hover:bg-[var(--lum-cream)]/40"
                  }`}
                />
                {active === i && (
                  <span
                    className="absolute inset-y-0 left-0 bg-[var(--lum-gold)]"
                    style={{ width: `${progress * 100}%` }}
                  />
                )}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            aria-label="次の質問"
            className="group w-10 h-10 flex items-center justify-center rounded-full border border-[var(--lum-cream)]/20 text-[var(--lum-cream)]/60 hover:text-[var(--lum-gold)] hover:border-[var(--lum-gold)] transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>

        <div className="mt-8 font-label text-[9px] text-[var(--lum-cream)]/35">
          {paused ? "PAUSED · MOVE AWAY TO RESUME" : "HOVER TO PAUSE"}
        </div>
      </Reveal>
    </section>
  );
}